<template>
  <div class="assets-grid-container">
    <!-- header slot -->
    <slot name="header"></slot>
    
    <div v-if="!$slots.footer" class="attachments-grid">
      <template v-for="asset in validAssets" :key="asset.id">
      <div class="attachment-wrapper">
        <!-- Delete button -->
        <div
          v-if="allowDelete"
          class="delete-button"
          @click.stop="handleDelete(asset)"
        >
          <el-icon>
            <font-awesome-icon :icon="['fas', 'trash']" />
          </el-icon>
        </div>

        <!-- Image assets -->
        <div v-if="asset.asset_type === 'image'" class="attachment-image">
          <div class="image-preview">
            <el-image
              :src="getAssetUrl(asset)"
              :preview-src-list="imageUrls"
              fit="cover"
              class="attachment-thumbnail"
              :initial-index="getAssetIndex(asset)"
              :preview-teleported="true"
              :append-to-body="true"
              @show="reloadImage(asset)"
            />
            <span class="image-name">{{ asset.name }}</span>
          </div>
        </div>

        <!-- PDF assets -->
        <div v-else-if="asset.asset_type === 'pdf'" class="attachment-pdf">
          <div class="pdf-preview" @click="openPdfModal(asset)">
            <div class="pdf-thumbnail">
              <el-icon class="pdf-icon"><Document /></el-icon>
            </div>
            <span class="pdf-name">{{ asset.name }}</span>
          </div>
        </div>

        <!-- Audio assets -->
        <div v-else-if="asset.asset_type === 'audio'" class="attachment-audio">
          <div class="audio-preview" @click="openAudioModal(asset)">
            <div class="audio-thumbnail">
              <el-icon class="audio-icon"><Headset /></el-icon>
            </div>
            <span class="audio-name">{{ asset.name }}</span>
          </div>
        </div>

        <!-- Video assets -->
        <div v-else-if="asset.asset_type === 'video'" class="attachment-video">
          <div class="video-preview" @click="openVideoModal(asset)">
            <div class="video-thumbnail">
              <video
                :src="asset.url"
                class="thumbnail-video"
                preload="metadata"
              >
                <el-icon class="video-icon"><VideoCamera /></el-icon>
              </video>
              <div class="video-overlay">
                <el-icon class="play-icon"><VideoPlay /></el-icon>
              </div>
            </div>
            <span class="video-name">{{ asset.name }}</span>
          </div>
        </div>

        <!-- Other file types -->
        <div v-else class="attachment-file">
          <div class="document-preview">
            <div class="document-thumbnail">
              <el-icon class="document-icon"><Document /></el-icon>
            </div>
            <span class="document-name">{{ asset.name }}</span>
          </div>
        </div>
        
        <slot name="asset-controls" :asset="asset" :index="validAssets.indexOf(asset)">
        </slot>
      </div>
    </template>

    <!-- PDF Modal -->
    <el-dialog
      v-model="showPdfModal"
      :title="selectedPdf?.name || 'PDF Preview'"
      width="90%"
      :before-close="handlePdfModalClose"
      class="pdf-modal"
      :append-to-body="true"
    >
      <iframe
        v-if="selectedPdf"
        :src="selectedPdf.url"
        class="pdf-iframe"
        :title="selectedPdf.name"
        style="min-height: 600px"
      />
    </el-dialog>

    <!-- Audio Modal -->
    <el-dialog
      v-model="showAudioModal"
      :title="selectedAudio?.name || 'Audio Player'"
      width="500px"
      :before-close="handleAudioModalClose"
      class="audio-modal"
      :append-to-body="true"
    >
      <div v-if="selectedAudio" class="audio-player-container">
        <audio
          ref="audioPlayer"
          :src="selectedAudio.url"
          controls
          class="audio-player"
        />
      </div>
    </el-dialog>

    <!-- Video Modal -->
    <el-dialog
      v-model="showVideoModal"
      :title="selectedVideo?.name || 'Video Player'"
      width="90%"
      :before-close="handleVideoModalClose"
      class="video-modal"
      :append-to-body="true"
    >
      <div v-if="selectedVideo" class="video-player-container">
        <video
          ref="videoPlayer"
          :src="selectedVideo.url"
          controls
          class="video-player"
        />
      </div>
    </el-dialog>
    </div>
    
    <!-- footer slot -->
    <slot name="footer" 
      :assets="validAssets" 
      :imageUrls="imageUrls"
      :handleDelete="handleDelete"
    ></slot>
  </div>
</template>
<script>
import {
  Picture,
  Document,
  WarningFilled,
  Headset,
  VideoCamera,
  VideoPlay,
} from '@element-plus/icons-vue';

import assetService from 'src/services/assetsServices.js';

export default {
  name: 'AssetsGrid',

  components: {
    Picture,
    Document,
    WarningFilled,
    Headset,
    VideoCamera,
    VideoPlay,
  },

  props: {
    assets: {
      type: Array,
      required: true,
      default: () => [],
    },
    allowDelete: {
      type: Boolean,
      default: false,
    },
    disableMessages: {
      type: Boolean,
      default: false,
    },
    showThumbnailControls: {
      type: Boolean,
      default: false,
    },
    thumbnailWidths: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      showPdfModal: false,
      selectedPdf: null,
      showAudioModal: false,
      selectedAudio: null,
      showVideoModal: false,
      selectedVideo: null,

      // we sample the images as we don't want to change the props directly
      sampledAssets: [],
      // This ensures we're not loading the images too frequently causing unnecessary load on the server or client
      imageLoadStates: new Map(),
    };
  },

  computed: {
    validAssets() {
      return (
        this.sampledAssets?.filter(
          (asset) => asset && typeof asset === 'object' && asset.url
        ) ?? []
      );
    },

    imageUrls() {
      return this.validAssets
        .filter((asset) => asset.asset_type === 'image')
        .map((asset) => this.getAssetUrl(asset));
    },
  },
  watch: {
    assets: {
      handler() {
        this.sampledAssets = this.assets;
      },
      deep: true,
    },
  },
  mounted() {
    this.sampledAssets = this.assets;
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  methods: {
    getAssetUrl(asset) {
      return asset.url;
    },

    getAssetIndex(asset) {
      return this.imageUrls.indexOf(asset.url);
    },

    shouldReloadImage(assetId) {
      const lastLoadTime = this.imageLoadStates.get(assetId);
      if (!lastLoadTime) return true;

      const tenMinutesInMs = 10 * 60 * 1000;
      const now = Date.now();
      return now - lastLoadTime >= tenMinutesInMs;
    },

    updateImageLoadTime(assetId) {
      this.imageLoadStates.set(assetId, Date.now());
    },

    /**
     * Reloads the image asset if enough time has passed since the last load
     * This ensures the image is available to viewed coming from the private bucket
     * but doesn't overload the server or client with too many requests
     */
    async reloadImage(asset) {
      // Only reload if enough time has passed
      if (!this.shouldReloadImage(asset.id)) {
        return;
      }

      try {
        const response = await assetService.fetchAsset(asset.id);
        const index = this.sampledAssets.findIndex((a) => a.id === asset.id);
        if (index !== -1) {
          this.updateImageLoadTime(asset.id);
          this.sampledAssets[index] = response.data;
        }
      } catch (error) {
        console.error('Error fetching asset:', error);
      }
    },

    // Asset operations
    handleDelete(asset) {
      this.$confirm('Are you sure you want to delete this asset?', 'Warning', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning',
      })
        .then(() => {
          this.$emit('delete-asset', asset);
          if (!this.disableMessages) {
            this.$message({
              type: 'success',
              message: 'Asset deleted successfully',
            });
          }
        })
        .catch(() => {
          if (!this.disableMessages) {
            this.$message({
              type: 'info',
              message: 'Delete canceled',
            });
          }
        });
    },
    
    updateThumbnailWidth(assetId, width) {
      this.$emit('update-thumbnail-width', assetId, width);
    },

    // Modal handling
    openPdfModal(asset) {
      this.selectedPdf = asset;
      this.showPdfModal = true;
    },

    handlePdfModalClose() {
      this.showPdfModal = false;
      this.selectedPdf = null;
    },

    openAudioModal(asset) {
      this.selectedAudio = asset;
      this.showAudioModal = true;
    },

    handleAudioModalClose() {
      const player = this.$refs.audioPlayer;
      if (player) {
        player.pause();
      }
      this.showAudioModal = false;
      this.selectedAudio = null;
    },
    openVideoModal(asset) {
      this.selectedVideo = asset;
      this.showVideoModal = true;
    },

    handleVideoModalClose() {
      const player = this.$refs.videoPlayer;
      if (player) {
        player.pause();
      }
      this.showVideoModal = false;
      this.selectedVideo = null;
    },
  },
};
</script>
<style scoped>
.assets-grid-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.attachments-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
  padding: 8px;
}

.attachment-wrapper {
  position: relative;
  isolation: isolate;
}

.delete-button {
  position: absolute;
  top: -4px;
  right: 4px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.2s;
}

.attachment-wrapper:hover .delete-button {
  opacity: 1;
}

.delete-button:hover {
  background-color: rgba(0, 0, 0, 0.7);
}

.attachment-thumbnail {
  width: 100px;
  height: 100px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
}

.attachment-image .el-image {
  width: 100%;
  height: 100%;
}

.attachment-image .el-image__inner {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.attachment-image,
.attachment-file,
.attachment-pdf,
.attachment-audio,
.attachment-video {
  padding: 8px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.image-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  width: 100%;
}

.image-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  max-width: 100%;
}

.pdf-preview,
.audio-preview,
.document-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  width: 100%;
}

.pdf-thumbnail,
.audio-thumbnail,
.document-thumbnail {
  width: 100px;
  height: 100px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  background-color: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pdf-icon,
.audio-icon,
.document-icon {
  color: #409eff;
  font-size: 32px;
}

.pdf-name,
.audio-name,
.video-name,
.image-name,
.document-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  max-width: 100%;
  text-align: center;
}

.image-placeholder,
.image-error {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f5f7fa;
  color: #909399;
}

.image-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: #fef0f0;
  color: #f56c6c;
  cursor: pointer;
  transition: background-color 0.2s;
}

.image-error:hover {
  background-color: #fde2e2;
}

.refresh-prompt {
  font-size: 12px;
  margin-top: 4px;
  color: #409eff;
}

.error-message {
  font-size: 12px;
  margin-top: 4px;
  color: #f56c6c;
}

.image-error .el-icon {
  font-size: 24px;
  margin-bottom: 4px;
}

.pdf-modal :deep(.el-dialog__body) {
  padding: 0;
  height: 80vh;
}

.pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.audio-player-container {
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.audio-player {
  width: 100%;
}

.audio-modal :deep(.el-dialog__body) {
  padding: 0;
}

.video-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  width: 100%;
}

.video-thumbnail {
  position: relative;
  width: 100px;
  height: 100px;
  background-color: #000;
  border-radius: 4px;
  overflow: hidden;
}

.thumbnail-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.video-overlay:hover {
  background-color: rgba(0, 0, 0, 0.5);
}

.play-icon {
  color: white;
  font-size: 24px;
}

.video-player-container {
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #000;
}

.video-player {
  width: 100%;
  max-height: 80vh;
}

.video-modal :deep(.el-dialog__body) {
  padding: 0;
}

:deep(.el-image-viewer__wrapper) {
  z-index: 9999 !important;
}

:deep(.el-image-viewer__mask) {
  z-index: 9998 !important;
}

.attachments-grid .el-image {
  display: block;
}
</style>
